import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Scissors,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Clock,
  Phone,
  Lock,
  KeyRound,
  Mail,
  XCircle,
  ArrowLeft,
} from "lucide-react";

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const WEEKDAYS = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
];
const BRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const toISO = (d) => d.toLocaleDateString("sv-SE");

const STATUS_LABEL = { confirmado: "Na fila", finalizado: "Finalizado", cancelado: "Cancelado" };
const STATUS_STYLE = {
  confirmado: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  finalizado: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  cancelado: "border-red-500/40 bg-red-500/10 text-red-400",
};

const PAYMENT_LABEL = {
  pago: "Pago online",
  pendente: "Pagamento pendente",
  no_local: "Pagar no local",
  expirado: "Pagamento expirado",
};

export default function BarberPage() {
  const [auth, setAuth] = useState(null); // null = verificando, false = deslogado
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [date, setDate] = useState(toISO(new Date()));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [view, setView] = useState("queue");
  const [reportPeriod, setReportPeriod] = useState("daily");
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setAuth(r.data))
      .catch(() => setAuth(false));
  }, []);

  const load = useCallback((d) => {
    setLoading(true);
    api
      .get(`/barber/bookings?date=${d}`)
      .then((r) => setBookings(r.data))
      .catch((e) => {
        if (e.response?.status === 401) setAuth(false);
        else toast.error("Erro ao carregar a fila");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (auth) load(date);
  }, [auth, date, load]);

  const loadReport = useCallback(() => {
    if (reportPeriod === "custom" && (!customStart || !customEnd)) {
      setReport(null);
      return;
    }
    setReportLoading(true);
    const qs =
      reportPeriod === "custom"
        ? `period=custom&start_date=${customStart}&end_date=${customEnd}`
        : `period=${reportPeriod}&date=${date}`;
    api
      .get(`/barber/report?${qs}`)
      .then((r) => setReport(r.data))
      .catch((e) => {
        if (e.response?.status === 401) setAuth(false);
        else toast.error("Erro ao carregar relatório");
      })
      .finally(() => setReportLoading(false));
  }, [reportPeriod, date, customStart, customEnd]);

  useEffect(() => {
    if (auth && view === "report") loadReport();
  }, [auth, view, loadReport]);

  async function login(e) {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setAuth(r.data);
      toast.success(`Bem-vindo, ${r.data.name}!`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha no login");
    } finally {
      setLoginLoading(false);
    }
  }

  async function logout() {
    await api.post("/auth/logout").catch(() => {});
    setAuth(false);
    toast.info("Sessão encerrada");
  }

  async function finish(id) {
    setActionId(id);
    try {
      await api.post(`/barber/bookings/${id}/finish`);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: "finalizado" } : b)));
      toast.success("Corte finalizado!");
    } catch {
      toast.error("Não foi possível finalizar");
    } finally {
      setActionId(null);
    }
  }

  async function cancel(id) {
    setActionId(id);
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: "cancelado" } : b)));
      toast.success("Agendamento cancelado");
    } catch {
      toast.error("Não foi possível cancelar");
    } finally {
      setActionId(null);
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    setPwLoading(true);
    try {
      await api.post("/barber/change-password", {
        current_password: curPw,
        new_password: newPw,
      });
      toast.success("Senha alterada com sucesso!");
      setCurPw("");
      setNewPw("");
      setShowPw(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível alterar a senha");
    } finally {
      setPwLoading(false);
    }
  }

  const shiftDay = (delta) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setDate(toISO(d));
  };

  const dObj = new Date(`${date}T12:00:00`);
  const closed = [0, 1].includes(dObj.getDay());
  const dateLabel = `${WEEKDAYS[dObj.getDay()]}, ${dObj.getDate()} de ${MONTHS[dObj.getMonth()]}`;
  const isToday = date === toISO(new Date());
  const queue = bookings.filter((b) => b.status === "confirmado");
  const done = bookings.filter((b) => b.status === "finalizado");
  const reportDays = report
    ? Object.entries(
        report.bookings.reduce((acc, b) => {
          (acc[b.date] = acc[b.date] || []).push(b);
          return acc;
        }, {})
      )
    : [];
  const tabCls = (active) =>
    `rounded-full py-2.5 text-xs font-bold uppercase tracking-wider transition-all ${
      active
        ? "bg-amber-500 text-zinc-950"
        : "border border-zinc-800 text-zinc-400 hover:border-amber-500/40"
    }`;
  const fmtDay = (iso) => {
    const [, m, d] = iso.split("-");
    return `${d}/${m}`;
  };

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0d0d0e]">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!auth) {
    return (
      <div
        data-testid="barber-login-page"
        className="flex min-h-screen items-center justify-center bg-[#0d0d0e] px-4"
      >
        <div className="w-full max-w-sm">
          <Link
            data-testid="back-to-site-link"
            to="/"
            className="mb-6 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500 transition-colors hover:text-amber-300"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar ao site
          </Link>
          <div className="rounded-2xl border border-amber-500/15 bg-[#161618] p-6">
            <div className="flex flex-col items-center text-center">
              <img
                src="/logo.png"
                alt="Logo Resenha dú Corte"
                className="h-16 w-16 rounded-full border border-amber-500/40 object-cover"
              />
              <h1 className="font-display mt-3 text-3xl text-amber-100">ÁREA DO BARBEIRO</h1>
              <p className="mt-1 text-xs text-zinc-500">Resenha dú Corte · acesso restrito</p>
            </div>
            <form onSubmit={login} className="mt-6 space-y-3">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
                <input
                  data-testid="barber-login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-mail"
                  autoComplete="email"
                  className="w-full rounded-xl border border-zinc-800 bg-[#0d0d0e] py-3 pl-11 pr-4 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-amber-500/60"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
                <input
                  data-testid="barber-login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha"
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-zinc-800 bg-[#0d0d0e] py-3 pl-11 pr-4 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-amber-500/60"
                />
              </div>
              <button
                data-testid="barber-login-submit-button"
                type="submit"
                disabled={loginLoading || !email || !password}
                className="gold-glow flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 py-3.5 text-sm font-bold uppercase tracking-wider text-zinc-950 transition-all hover:bg-amber-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
              >
                {loginLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loginLoading ? "Entrando..." : "Entrar"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="barber-dashboard" className="min-h-screen bg-[#0d0d0e]">
      <header className="sticky top-0 z-50 border-b border-amber-500/15 bg-[#0d0d0e]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3 md:max-w-2xl">
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Logo Resenha dú Corte"
              className="h-10 w-10 rounded-full border border-amber-500/40 object-cover"
            />
            <div>
              <p className="font-display text-lg leading-none text-amber-100">RESENHA DÚ CORTE</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">
                Fila de {auth.name}
              </p>
            </div>
          </div>
          <button
            data-testid="barber-change-password-toggle"
            onClick={() => setShowPw((v) => !v)}
            className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 py-2 text-xs font-semibold text-zinc-400 transition-colors hover:border-amber-500/50 hover:text-amber-300"
          >
            <KeyRound className="h-3.5 w-3.5" /> Senha
          </button>
          <button
            data-testid="barber-logout-button"
            onClick={logout}
            className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 py-2 text-xs font-semibold text-zinc-400 transition-colors hover:border-red-500/50 hover:text-red-400"
          >
            <LogOut className="h-3.5 w-3.5" /> Sair
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 py-6 md:max-w-2xl">
        <div className="mb-4 grid grid-cols-2 gap-2">
          <button
            data-testid="barber-tab-queue"
            onClick={() => setView("queue")}
            className={tabCls(view === "queue")}
          >
            Fila do dia
          </button>
          <button
            data-testid="barber-tab-report"
            onClick={() => setView("report")}
            className={tabCls(view === "report")}
          >
            Relatório
          </button>
        </div>

        {view === "queue" && (
          <>
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-center">
            <p data-testid="barber-queue-count" className="font-display text-3xl text-amber-400">
              {queue.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Na fila</p>
          </div>
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-center">
            <p data-testid="barber-done-count" className="font-display text-3xl text-emerald-400">
              {done.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              Finalizados
            </p>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-[#161618] p-3 text-center">
            <p data-testid="barber-total-count" className="font-display text-3xl text-zinc-200">
              {bookings.filter((b) => b.status !== "cancelado").length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Total</p>
          </div>
        </div>

        {showPw && (
          <form
            data-testid="barber-change-password-form"
            onSubmit={changePassword}
            className="mt-4 space-y-3 rounded-xl border border-amber-500/20 bg-[#161618] p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-amber-400">
              Alterar senha
            </p>
            <input
              data-testid="barber-current-password-input"
              type="password"
              value={curPw}
              onChange={(e) => setCurPw(e.target.value)}
              placeholder="Senha atual"
              autoComplete="current-password"
              className="w-full rounded-xl border border-zinc-800 bg-[#0d0d0e] px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-amber-500/60"
            />
            <input
              data-testid="barber-new-password-input"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="Nova senha (mín. 6 caracteres)"
              autoComplete="new-password"
              className="w-full rounded-xl border border-zinc-800 bg-[#0d0d0e] px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-amber-500/60"
            />
            <button
              data-testid="barber-change-password-submit"
              type="submit"
              disabled={pwLoading || !curPw || newPw.length < 6}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 py-3 text-xs font-bold uppercase tracking-wider text-zinc-950 transition-all hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {pwLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar nova senha
            </button>
          </form>
        )}

        <div className="mt-5 flex items-center justify-between rounded-xl border border-amber-500/15 bg-[#161618] p-2">
          <button
            data-testid="barber-date-prev"
            onClick={() => shiftDay(-1)}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-amber-300"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="text-center">
            <p data-testid="barber-date-label" className="text-sm font-semibold capitalize text-zinc-100">
              {isToday ? "Hoje · " : ""}
              {dateLabel}
            </p>
            {closed && (
              <p
                data-testid="barber-closed-badge"
                className="text-[10px] font-bold uppercase tracking-wider text-red-400"
              >
                Barbearia fechada
              </p>
            )}
          </div>
          <button
            data-testid="barber-date-next"
            onClick={() => shiftDay(1)}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-amber-300"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        {!isToday && (
          <button
            data-testid="barber-today-button"
            onClick={() => setDate(toISO(new Date()))}
            className="mt-2 w-full rounded-lg border border-amber-500/30 py-2 text-[10px] font-bold uppercase tracking-wider text-amber-400 transition-colors hover:bg-amber-500/10"
          >
            Voltar para hoje
          </button>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-14 text-sm text-zinc-400">
            <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> Carregando fila...
          </div>
        ) : bookings.length === 0 ? (
          <p
            data-testid="barber-empty-queue"
            className="mt-6 rounded-xl border border-zinc-800 bg-[#161618] p-6 text-center text-sm text-zinc-400"
          >
            Nenhum agendamento neste dia.
          </p>
        ) : (
          <div data-testid="barber-queue-list" className="mt-4 space-y-3">
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`barber-booking-card-${b.id}`}
                className={`rounded-xl border p-4 ${
                  b.status === "cancelado"
                    ? "border-zinc-800/60 bg-zinc-900/40 opacity-60"
                    : b.status === "finalizado"
                      ? "border-emerald-500/20 bg-[#161618]"
                      : "border-amber-500/20 bg-[#161618]"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-center rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5">
                      <Clock className="h-3 w-3 text-amber-400" />
                      <span className="mt-0.5 text-sm font-bold text-amber-300">{b.time}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-zinc-100">{b.name}</p>
                      <p className="text-xs text-zinc-400">
                        {(b.service_names?.length ? b.service_names : [b.service_name])
                          .filter(Boolean)
                          .join(" + ")}
                      </p>
                      <p className="mt-1 flex items-center gap-1 text-xs text-zinc-500">
                        <Phone className="h-3 w-3" /> {b.phone}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider ${STATUS_STYLE[b.status] || ""}`}
                  >
                    {STATUS_LABEL[b.status] || b.status}
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-zinc-800/60 pt-3">
                  <p className="text-xs text-zinc-400">
                    <span className="font-semibold text-amber-400">{BRL(b.price)}</span>
                    {" · "}
                    {PAYMENT_LABEL[b.payment_status] || b.payment_status}
                    {" · ~"}
                    {b.duration_min || 30} min
                  </p>
                  {b.status === "confirmado" && (
                    <div className="flex gap-2">
                      <button
                        data-testid={`finish-booking-${b.id}`}
                        onClick={() => finish(b.id)}
                        disabled={actionId === b.id}
                        className="flex items-center gap-1 rounded-full bg-emerald-500 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-950 transition-all hover:bg-emerald-400 active:scale-95 disabled:opacity-40"
                      >
                        {actionId === b.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3" />
                        )}
                        Finalizar
                      </button>
                      <button
                        data-testid={`barber-cancel-booking-${b.id}`}
                        onClick={() => cancel(b.id)}
                        disabled={actionId === b.id}
                        className="flex items-center gap-1 rounded-full border border-red-500/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-40"
                      >
                        <XCircle className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}

        {view === "report" && (
          <div data-testid="barber-report">
            <div className="grid grid-cols-4 gap-2">
              {[
                ["daily", "Dia"],
                ["weekly", "Semana"],
                ["monthly", "Mês"],
                ["custom", "Período"],
              ].map(([p, label]) => (
                <button
                  key={p}
                  data-testid={`report-period-${p}`}
                  onClick={() => setReportPeriod(p)}
                  className={tabCls(reportPeriod === p)}
                >
                  {label}
                </button>
              ))}
            </div>

            {reportPeriod === "custom" && (
              <div data-testid="report-custom-range" className="mt-3 grid grid-cols-2 gap-2">
                <div>
                  <label className="eyebrow !text-[10px]" htmlFor="report-start">
                    De
                  </label>
                  <input
                    id="report-start"
                    data-testid="report-start-date"
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-zinc-800 bg-[#0d0d0e] px-3 py-2.5 text-sm text-zinc-100 outline-none focus:border-amber-500/60"
                  />
                </div>
                <div>
                  <label className="eyebrow !text-[10px]" htmlFor="report-end">
                    Até
                  </label>
                  <input
                    id="report-end"
                    data-testid="report-end-date"
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-zinc-800 bg-[#0d0d0e] px-3 py-2.5 text-sm text-zinc-100 outline-none focus:border-amber-500/60"
                  />
                </div>
              </div>
            )}

            {reportPeriod === "custom" && !customStart && !customEnd && !reportLoading && (
              <p
                data-testid="report-custom-hint"
                className="mt-4 rounded-xl border border-zinc-800 bg-[#161618] p-6 text-center text-sm text-zinc-400"
              >
                Escolha as datas de início e fim para gerar o relatório.
              </p>
            )}

            {reportLoading ? (
              <div className="flex items-center justify-center gap-2 py-14 text-sm text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> Gerando relatório...
              </div>
            ) : (
              report && (
                <>
                  <div
                    data-testid="report-total-card"
                    className="gold-glow mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 text-center"
                  >
                    <p className="eyebrow">Faturamento realizado</p>
                    <p
                      data-testid="report-total"
                      className="font-display mt-1 text-5xl text-amber-400"
                    >
                      {BRL(report.total)}
                    </p>
                    <p data-testid="report-count" className="mt-1 text-xs text-zinc-400">
                      {report.count} atendimento{report.count === 1 ? "" : "s"} finalizado
                      {report.count === 1 ? "" : "s"}
                    </p>
                    <div className="mt-3 flex justify-center gap-4 text-[10px] font-bold uppercase tracking-wider">
                      <span data-testid="report-paid-online" className="text-emerald-400">
                        Online pago: {BRL(report.paid_online)}
                      </span>
                      <span data-testid="report-scheduled" className="text-zinc-400">
                        Na fila: {BRL(report.scheduled)}
                      </span>
                    </div>
                    <p className="mt-3 text-[10px] uppercase tracking-wider text-zinc-600">
                      Período: {fmtDay(report.start)} a {fmtDay(report.end)}
                    </p>
                  </div>

                  <div className="mt-4 space-y-4">
                    {reportDays.length === 0 && (
                      <p
                        data-testid="report-empty"
                        className="rounded-xl border border-zinc-800 bg-[#161618] p-6 text-center text-sm text-zinc-400"
                      >
                        Nenhum atendimento no período.
                      </p>
                    )}
                    {reportDays.map(([day, items]) => (
                      <div key={day}>
                        <p className="eyebrow mb-2 !text-[10px]">
                          {fmtDay(day)} ·{" "}
                          {BRL(
                            items
                              .filter((i) => i.status === "finalizado")
                              .reduce((s, i) => s + i.price, 0)
                          )}{" "}
                          realizado
                        </p>
                        <div className="space-y-2">
                          {items.map((b) => (
                            <div
                              key={b.id}
                              data-testid={`report-booking-${b.id}`}
                              className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-[#161618] p-3"
                            >
                              <div>
                                <p className="text-sm font-semibold text-zinc-100">{b.name}</p>
                                <p className="text-xs text-zinc-400">
                                  {(b.service_names?.length ? b.service_names : [b.service_name])
                                    .filter(Boolean)
                                    .join(" + ")}
                                </p>
                                <p className="mt-0.5 text-[10px] uppercase tracking-wider text-zinc-500">
                                  {b.time} ·{" "}
                                  {b.status === "finalizado" ? "Finalizado" : "Agendado"} ·{" "}
                                  {PAYMENT_LABEL[b.payment_status] || b.payment_status}
                                </p>
                              </div>
                              <span
                                className={`font-display shrink-0 text-xl ${
                                  b.status === "finalizado" ? "text-amber-400" : "text-zinc-600"
                                }`}
                              >
                                {BRL(b.price)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )
            )}
          </div>
        )}
      </main>
    </div>
  );
}
